import { numIsInteger } from './ast.js';
import { FormulaErrorCodes } from './errors.js';
import * as runtime from './runtime.js';
import { Types, describeResult, isNumericResult, isTemporal, resultType } from './types.js';
import { requireDate, requireDecimal, truthy } from './values.js';
import { sameOrPlain } from './value-types.js';
import { emitted } from './emit/emitted.js';
export function arityText(fn) {
    return fn.minArgs === fn.maxArgs ? String(fn.minArgs) : `${fn.minArgs} to ${fn.maxArgs}`;
}
export function acceptsArity(fn, call) {
    return call.args.length >= fn.minArgs && call.args.length <= fn.maxArgs;
}
/** The `where:` argument of an aggregate call, when given. */
export function whereOf(call) {
    return call.named.find((n) => n.name === 'where')?.value;
}
function define({ name, arity: [minArgs, maxArgs], row, aggregate, named }) {
    return { name, minArgs, maxArgs, namedParameters: new Set(named ?? []), row: row ?? null, aggregate: aggregate ?? null };
}
// ── row functions ─────────────────────────────────────────────────────────────
/** The literal decimals of a checked `round()` call. */
function roundDecimals(call) {
    return parseInt(call.args[1].value, 10);
}
/** The mode a checked `round()` call names, or the default the host gave (§4.6). */
function roundMode(call, defaultRounding) {
    return call.args.length === 3 ? call.args[2].value : defaultRounding;
}
/** `round(x, n [, mode])`: the literal decimals and the named or default mode (§4.6); null stays null. */
function evaluateRound({ call, arg, options }) {
    const x = arg(0);
    return x === null ? null : runtime.round(requireDecimal(x, 'round'), roundDecimals(call), roundMode(call, options.rounding));
}
function evaluateMinMax({ call, arg }) {
    const [a, b] = [arg(0), arg(1)];
    if (a === null || b === null)
        return null;
    const x = requireDecimal(a, call.fn);
    const y = requireDecimal(b, call.fn);
    return call.fn === 'min' ? runtime.min(x, y) : runtime.max(x, y);
}
/** C#: `FormulaArithmetic.Round(x, 2, FormulaRoundingMode.HalfEven)`, lifted through `FormulaRuntime` for a nullable x. */
function emitRoundCSharp(ctx) {
    const mode = roundMode(ctx.call, ctx.defaultRounding) === 'halfUp' ? 'HalfUp' : 'HalfEven';
    return ctx.numericCall('FormulaArithmetic.Round', 'FormulaRuntime.Round', [ctx.arg(0)], [String(roundDecimals(ctx.call)), `FormulaRoundingMode.${mode}`]);
}
/** TypeScript: `F.round(x, 2, 'halfEven')`. */
function emitRoundTypeScript(ctx) {
    return ctx.runtimeCall('round', [ctx.arg(0)], 'number', [String(roundDecimals(ctx.call)), JSON.stringify(roundMode(ctx.call, ctx.defaultRounding))]);
}
const emitIf = {
    csharp: (ctx) => ctx.conditional(ctx.arg(0), ctx.arg(1, ctx.result.type), ctx.arg(2, ctx.result.type)),
    typescript: (ctx) => ctx.conditional(ctx.arg(0), ctx.arg(1, ctx.result.type), ctx.arg(2, ctx.result.type)),
};
/** A one- or two-argument numeric function: `Math.X` / `FormulaRuntime.X` in C#, `F.x` in TypeScript. */
function emitNumeric(csharpName, typescriptName, arity) {
    const args = (ctx) => (arity === 1 ? [ctx.arg(0)] : [ctx.arg(0), ctx.arg(1)]);
    return {
        csharp: (ctx) => ctx.numericCall(`Math.${csharpName}`, `FormulaRuntime.${csharpName}`, args(ctx)),
        typescript: (ctx) => ctx.runtimeCall(typescriptName, args(ctx), 'number'),
    };
}
/** `round(x, n [, mode])`: numeric x, a literal integer n, an optional literal mode; the result has scale n (§4.6). */
function inferRound(call) {
    const x = requireNumeric(call, 0);
    if (!x)
        return null;
    const decimals = call.call.args[1];
    if (decimals.kind !== 'num' || !isRoundScale(decimals)) {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, decimals.span, `round() needs a literal whole number of decimals from 0 to ${MAX_ROUND_DECIMALS}.`, {
            argument: 'decimals',
        });
        return null;
    }
    if (call.call.args.length === 3 && !isRoundingMode(call.call.args[2])) {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, call.call.args[2].span, "round() takes the mode 'halfEven' or 'halfUp'.", {
            argument: 'mode',
        });
        return null;
    }
    return { ...x, type: Types.Decimal, scale: parseInt(decimals.value, 10) };
}
/** The most decimals `round()` takes: what System.Decimal holds. */
const MAX_ROUND_DECIMALS = 28;
/** A whole number of decimals from 0 to 28, compared as text first so no literal is too long to read. */
function isRoundScale(node) {
    const digits = node.value.replace(/^0+(?=\d)/, '');
    return numIsInteger(node) && digits.length <= 2 && Number(digits) <= MAX_ROUND_DECIMALS;
}
function isRoundingMode(node) {
    return node.kind === 'str' && (node.value === 'halfEven' || node.value === 'halfUp');
}
/** `min(a, b)`, `max(a, b)`: two numbers of one value type (§4.7); null if either is null. The `where:` belongs to the collection form. */
function inferMinMax(call) {
    const named = call.call.named[0];
    if (named) {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, named.value.span, `${call.call.fn}(a, b) takes no named argument; '${named.name}:' belongs to ${call.call.fn}(collection, expression).`, {
            function: call.call.fn,
            name: named.name,
        });
        return null;
    }
    const a = requireNumeric(call, 0);
    const b = requireNumeric(call, 1);
    if (!a || !b)
        return null;
    let left = a;
    if (a.valueType !== b.valueType && !sameOrPlain(a.valueType, b.valueType)) {
        call.checker.error(FormulaErrorCodes.ValueTypeMismatch, call.call.span, `${call.call.fn}() needs one value type; got ${a.valueType} and ${b.valueType}.`, { left: a.valueType, right: b.valueType });
        left = { ...a, valueType: 'Unknown' }; // reported once; the call carries Unknown from here
    }
    return call.checker.merge(call.call, call.call.args[0], left, call.call.args[1], b, a.nullable || b.nullable);
}
/** `if(cond, a, b)`: as `?:`. */
function inferIf(call) {
    const condition = call.args[0];
    if (condition.type?.base !== 'Boolean') {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, call.call.args[0].span, `The condition of if() must be a Boolean; got ${describeResult(condition)}.`, { operand: describeResult(condition) });
        return null;
    }
    return call.checker.merge(call.call, call.call.args[1], call.args[1], call.call.args[2], call.args[2], call.args[1].nullable || call.args[2].nullable);
}
function requireNumeric(call, index) {
    const arg = call.args[index];
    if (isNumericResult(arg))
        return arg;
    call.checker.error(FormulaErrorCodes.BaseTypeMismatch, call.call.args[index].span, `${call.call.fn}() needs a number; got ${describeResult(arg)}.`, {
        operand: describeResult(arg),
    });
    return null;
}
// ── dates (§4.7) ──────────────────────────────────────────────────────────────
/**
 * `addDays(d, n)`, `addMonths(d, n)`: a Date or DateTime and a whole number (an Integer — a count of days is
 * never a fraction); the result is the date's own type, null when either argument is.
 */
function inferAddDate(call) {
    const date = call.args[0];
    if (!date.type || !isTemporal(date.type)) {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, call.call.args[0].span, `${call.call.fn}() needs a Date or DateTime; got ${describeResult(date)}.`, {
            operand: describeResult(date),
        });
        return null;
    }
    const count = call.args[1];
    if (count.type?.base !== 'Integer') {
        call.checker.error(FormulaErrorCodes.BaseTypeMismatch, call.call.args[1].span, `${call.call.fn}() needs a whole number; got ${describeResult(count)}.`, {
            operand: describeResult(count),
        });
        return null;
    }
    return resultType(date.type, 'Plain', date.nullable || count.nullable, null);
}
/** One date function: the runtime's `add` over a date and a whole number, `FormulaArithmetic.X` / `FormulaRuntime.X` in C#. */
function dateFunction(name, method, add) {
    return {
        infer: inferAddDate,
        evaluate: ({ arg }) => {
            const [date, count] = [arg(0), arg(1)];
            return date === null || count === null ? null : add(requireDate(date, name), requireDecimal(count, name));
        },
        emit: {
            csharp: (ctx) => ctx.liftedCall(`FormulaArithmetic.${method}`, `FormulaRuntime.${method}`, [ctx.arg(0), ctx.arg(1)], 'date'),
            typescript: (ctx) => ctx.runtimeCall(name, [ctx.arg(0), ctx.arg(1)], 'date'),
        },
    };
}
// ── aggregates (§4.7) ─────────────────────────────────────────────────────────
/** The expression argument of an aggregate, inferred in the collection's scope; it must be numeric. */
function inferNumericBody(agg) {
    const node = agg.call.args[1];
    const type = agg.scope.infer(node);
    if (!type)
        return null;
    if (isNumericResult(type))
        return type;
    agg.checker.error(FormulaErrorCodes.BaseTypeMismatch, node.span, `${agg.call.fn}() aggregates numbers; got ${describeResult(type)}.`, {
        operand: describeResult(type),
    });
    return null;
}
function requireBooleanBody(agg, node, type, what) {
    if (type.type?.base === 'Boolean')
        return true;
    agg.checker.error(FormulaErrorCodes.BaseTypeMismatch, node.span, `${what} of ${agg.call.fn}() must be a Boolean; got ${describeResult(type)}.`, {
        operand: describeResult(type),
    });
    return false;
}
/** The `where:` argument, inferred in the collection's scope when given; true when it is absent or a Boolean. */
function inferWhere(agg) {
    const where = whereOf(agg.call);
    if (!where)
        return true;
    const type = agg.scope.infer(where);
    return type !== null && requireBooleanBody(agg, where, type, 'The where:');
}
/** `any(coll, pred)`, `all(coll, pred)`: a Boolean predicate over the rows; the result is never null. */
function inferPredicateAggregate(agg) {
    const node = agg.call.args[1];
    const type = agg.scope.infer(node);
    if (!type || !requireBooleanBody(agg, node, type, 'The predicate'))
        return null;
    return resultType(Types.Boolean, 'Plain', false, null);
}
/** `sum(coll, expr [, where])`: the expression's type, value type, scale and measure; never null — 0 when empty, nulls skipped. */
function inferSum(agg) {
    const expr = inferNumericBody(agg);
    const whereOk = inferWhere(agg);
    return expr && whereOk ? { ...expr, nullable: false } : null;
}
/** `count(coll [, where])`: an Integer, never null. */
function inferCount(agg) {
    return inferWhere(agg) ? resultType(Types.Integer, 'Plain', false, 0) : null;
}
/** `min(coll, expr [, where])`, `max(…)`: as the expression, and null when nothing is left. */
function inferExtreme(agg) {
    const expr = inferNumericBody(agg);
    const whereOk = inferWhere(agg);
    return expr && whereOk ? { ...expr, nullable: true } : null;
}
/** The body argument at `index` as a per-row number: a checked tree's body is numeric or null. */
function bodyNumber(evaluation, index) {
    const node = evaluation.call.args[index];
    return (row) => {
        const value = evaluation.evaluate(node, row);
        return value === null ? null : requireDecimal(value, evaluation.call.fn);
    };
}
/** A body node as a per-row predicate: its Boolean with null counting as false (§4.4). */
function bodyPredicate(evaluation, node) {
    return (row) => truthy(evaluation.evaluate(node, row));
}
function wherePredicate(evaluation) {
    const where = whereOf(evaluation.call);
    return where ? bodyPredicate(evaluation, where) : undefined;
}
/** The call's arguments in both languages: the rows, the body as a lambda, and the `where:` predicate when given. */
function aggregateArgs(ctx, body) {
    const args = [ctx.rows];
    if (body === 'value')
        args.push(ctx.lambda(1));
    if (body === 'predicate')
        args.push(ctx.predicate(1));
    const where = ctx.where();
    if (where)
        args.push(where);
    return args;
}
/**
 * C#: `FormulaRuntime.Sum(rows, row => …, row => …)`; TypeScript: `F.sum(rows, (row) => …, (row) => …)`; SQL: the
 * grouped-query fragment `sql` builds over the group variable (`g.Sum(l => …) ?? 0m`).
 */
function aggregateTemplates(csharpName, typescriptName, kind, nullable, body, sql) {
    return {
        csharp: (ctx) => emitted(`FormulaRuntime.${csharpName}(${aggregateArgs(ctx, body).join(', ')})`, kind, nullable),
        typescript: (ctx) => emitted(`${ctx.runtime}.${typescriptName}(${aggregateArgs(ctx, body).join(', ')})`, kind, nullable),
        sql: (ctx) => emitted(sql(ctx), kind, nullable),
    };
}
/** `SUM(CASE WHEN where THEN expr END)` skips nulls and is null over nothing: the `?? 0m` is the empty result of §4.7. */
const sum = {
    infer: inferSum,
    evaluate: (e) => runtime.sum(e.rows, bodyNumber(e, 1), wherePredicate(e)),
    emit: aggregateTemplates('Sum', 'sum', 'number', false, 'value', (ctx) => `(${ctx.group}.Sum(${ctx.value(1)}) ?? 0m)`),
};
/** `COUNT(*)`, or with a `where:` the rows it keeps counted as ones; a number, as every formula number is. */
const count = {
    infer: inferCount,
    evaluate: (e) => runtime.count(e.rows, wherePredicate(e)),
    emit: aggregateTemplates('Count', 'count', 'number', false, null, (ctx) => {
        const where = ctx.where();
        return where ? `(decimal)${ctx.group}.Sum(${ctx.row} => (${where} ? 1 : 0))` : `(decimal)${ctx.group}.Count()`;
    }),
};
/** `MIN(CASE WHEN where THEN expr END)`: skips nulls, null over nothing. */
const min = {
    infer: inferExtreme,
    evaluate: (e) => runtime.min(e.rows, bodyNumber(e, 1), wherePredicate(e)),
    emit: aggregateTemplates('Min', 'min', 'number', true, 'value', (ctx) => `${ctx.group}.Min(${ctx.value(1)})`),
};
const max = {
    infer: inferExtreme,
    evaluate: (e) => runtime.max(e.rows, bodyNumber(e, 1), wherePredicate(e)),
    emit: aggregateTemplates('Max', 'max', 'number', true, 'value', (ctx) => `${ctx.group}.Max(${ctx.value(1)})`),
};
/** Some row satisfies the predicate: `SUM(CASE WHEN pred THEN 1 ELSE 0 END) > 0`, false over nothing. */
const any = {
    infer: inferPredicateAggregate,
    evaluate: (e) => runtime.any(e.rows, bodyPredicate(e, e.call.args[1])),
    emit: aggregateTemplates('Any', 'any', 'boolean', false, 'predicate', (ctx) => `(${ctx.group}.Sum(${ctx.row} => (${ctx.condition(1)} ? 1 : 0)) > 0)`),
};
/** No row fails the predicate: `SUM(CASE WHEN pred THEN 0 ELSE 1 END) == 0`, true over nothing. */
const all = {
    infer: inferPredicateAggregate,
    evaluate: (e) => runtime.all(e.rows, bodyPredicate(e, e.call.args[1])),
    emit: aggregateTemplates('All', 'all', 'boolean', false, 'predicate', (ctx) => `(${ctx.group}.Sum(${ctx.row} => (${ctx.condition(1)} ? 0 : 1)) == 0)`),
};
// ── the registry ──────────────────────────────────────────────────────────────
/**
 * The functions the type checker knows: the row functions of §4.7 (`round` bounds a scale, `if` mirrors `?:`) and
 * the aggregates; `min` and `max` have both forms; `addDays` and `addMonths` (Computed Fields Phase 3). The string
 * and planning functions are registered with their implementations in later phases.
 */
const FUNCTIONS = new Map([
    define({ name: 'round', arity: [2, 3], row: { infer: inferRound, evaluate: evaluateRound, emit: { csharp: emitRoundCSharp, typescript: emitRoundTypeScript } } }),
    define({
        name: 'abs',
        arity: [1, 1],
        row: { infer: (call) => requireNumeric(call, 0), evaluate: ({ arg }) => {
                const x = arg(0);
                return x === null ? null : runtime.abs(requireDecimal(x, 'abs'));
            }, emit: emitNumeric('Abs', 'abs', 1) },
    }),
    define({ name: 'min', arity: [2, 2], row: { infer: inferMinMax, evaluate: evaluateMinMax, emit: emitNumeric('Min', 'min', 2) }, aggregate: min, named: ['where'] }),
    define({ name: 'max', arity: [2, 2], row: { infer: inferMinMax, evaluate: evaluateMinMax, emit: emitNumeric('Max', 'max', 2) }, aggregate: max, named: ['where'] }),
    define({ name: 'if', arity: [3, 3], row: { infer: inferIf, evaluate: ({ arg }) => (truthy(arg(0)) ? arg(1) : arg(2)), emit: emitIf } }),
    define({ name: 'addDays', arity: [2, 2], row: dateFunction('addDays', 'AddDays', runtime.addDays) }),
    define({ name: 'addMonths', arity: [2, 2], row: dateFunction('addMonths', 'AddMonths', runtime.addMonths) }),
    define({ name: 'sum', arity: [2, 2], aggregate: sum, named: ['where'] }),
    define({ name: 'count', arity: [1, 1], aggregate: count, named: ['where'] }),
    define({ name: 'any', arity: [2, 2], aggregate: any }),
    define({ name: 'all', arity: [2, 2], aggregate: all }),
].map((fn) => [fn.name, fn]));
export function findFunction(name) {
    return FUNCTIONS.get(name);
}
export function allFunctions() {
    return [...FUNCTIONS.values()];
}
